import React, { useState } from 'react';
import { useLanguage } from '../i18n';
import { Send, Trash2, Lightbulb, Clock, Mail, History, Sparkles, MessageSquare, List } from 'lucide-react';
import { Idea } from '../types';

interface IdeasViewProps {
  ideas: Idea[];
  onAddIdea: (text: string) => void;
  onDeleteIdea: (id: string) => void;
}

export const IdeasView: React.FC<IdeasViewProps> = ({ ideas, onAddIdea, onDeleteIdea }) => {
  const { t } = useLanguage();
  const [text, setText] = useState('');
  const [activeTab, setActiveTab] = useState<'feed' | 'updates'>('feed');

  // Comprehensive update log containing the full history of recent significant changes
  const updateLog = [
    {
      date: "v1.2.3 - Paper Accumulation Update",
      version: "v1.2.3",
      changes: [
        "Reimagined bucket filling for 'Experimental Original Design': Points now accumulate as a heap of 'paper trash' pieces.",
        "Pieces fall into the bucket with a physics-inspired entrance animation.",
        "Accumulation avoids a strict 100% ceiling, focusing on organic volume growth rather than a 'water level'.",
        "Deterministic consistency: The heap layout stays stable for each specific point count."
      ]
    },
    {
      date: "v1.2.2 - Experimental Playground",
      version: "v1.2.2",
      changes: [
        "Added 'Experimental Original Design' variant: A safe playground for testing new UI elements.",
        "Removed 'Time Limited' bucket type to simplify mission creation (consolidated into Fokus Sprint).",
        "Redesigned the Fokus Sprint header with a pulsing mission banner and higher visual impact.",
        "Improved cross-linking labels in All Tasks view."
      ]
    },
    {
      date: "v1.2.1 - Mission Control Update",
      version: "v1.2.1",
      changes: [
        "Consolidated 'Time Limited' and 'Fokus Sprint' into a single, powerful mission-based bucket type.",
        "Added a high-impact 'Mission Banner' to Fokus Sprint headers for maximum visual distinction.",
        "Refined Fokus Sprint countdown timer visibility in the header.",
        "Streamlined the Bucket creation modal with a clearer type selector."
      ]
    },
    {
      date: "v1.2.0 - Sprint & Polish",
      version: "v1.2.0",
      changes: [
        "Added 'Fokus Sprint' bucket type: Create goal-driven missions with a name, description, and strict dates.",
        "Fixed major visibility issues in Flat Design (Grid, All Tasks, and Ideas views).",
        "Mapped light text colors to high-contrast slate colors when in Flat mode for readability.",
        "Curated 5 professional 'Color Moods' for the Flat Design (Sunset, Ocean, Cyber, Berry, Desert).",
        "Improved structural contrast in Flat Design with solid backgrounds and thick borders."
      ]
    },
    {
      date: "v1.1.5 - Visual Progress",
      version: "v1.1.5",
      changes: [
        "Redesigned the Level Badge with a liquid progress-track visualization.",
        "Implemented a vertical scrollable slider for choosing task repetitions in the task modal.",
        "Tasks now automatically disappear from the main Bucket View once they are completed.",
        "Added 'Done tasks' filter toggle to the 'All Tasks' view."
      ]
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    onAddIdea(text);
    setText('');
  };

  const handleSendEmail = (ideaText: string) => {
    const subject = encodeURIComponent("LifeBuckets Idea / Feedback");
    const body = encodeURIComponent(ideaText);
    window.location.href = `mailto:draftpaper@outlook.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center text-yellow-400">
              <Lightbulb size={24} />
          </div>
          <h2 className="text-2xl font-bold text-white">{t.ideasView}</h2>
        </div>
        
        <div className="flex bg-slate-900/50 p-1 rounded-xl border border-white/10">
          <button 
            onClick={() => setActiveTab('feed')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'feed' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
          >
            <MessageSquare size={14} />
            Feed
          </button>
          <button 
            onClick={() => setActiveTab('updates')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'updates' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
          >
            <History size={14} />
            Updates Log
          </button>
        </div>
      </div>

      {activeTab === 'feed' ? (
        <div className="flex-1 flex flex-col">
          <form onSubmit={handleSubmit} className="mb-8 relative shrink-0">
            <div className="relative">
                <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t.ideaPlaceholder}
                className="w-full bg-slate-900/50 border border-white/10 rounded-2xl p-4 pr-14 text-white placeholder:text-slate-500 focus:outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/50 transition-all resize-none h-32 custom-scrollbar"
                />
                <button
                type="submit"
                disabled={!text.trim()}
                className="absolute bottom-3 right-3 p-2 bg-yellow-500 hover:bg-yellow-400 text-slate-900 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 shadow-lg shadow-yellow-500/20"
                >
                <Send size={18} />
                </button>
            </div>
          </form>

          <div className="space-y-4 flex-1 overflow-y-auto custom-scrollbar pb-10">
            {ideas.length === 0 ? (
                <div className="text-center py-20 text-slate-500 border-2 border-dashed border-white/5 rounded-2xl">
                    <Lightbulb size={48} className="mx-auto mb-4 opacity-20" />
                    <p>{t.noIdeas}</p>
                </div>
            ) : (
                ideas.map((idea) => (
                    <div key={idea.id} className="bg-slate-900/50 border border-white/5 p-4 rounded-xl flex gap-4 group hover:border-white/10 transition-colors animate-in fade-in slide-in-from-top-2">
                        <div className="flex-1 min-w-0">
                            <p className="text-slate-200 whitespace-pre-wrap leading-relaxed">{idea.text}</p>
                            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                                <Clock size={12} />
                                {new Date(idea.createdAt).toLocaleString()}
                            </p>
                        </div>
                        <div className="flex flex-col gap-1 self-start opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                            <button
                                onClick={() => handleSendEmail(idea.text)}
                                className="p-2 text-slate-500 hover:text-indigo-400 hover:bg-white/5 rounded-lg transition-colors"
                                title="Send via Email"
                            >
                                <Mail size={16} />
                            </button>
                            <button
                                onClick={() => onDeleteIdea(idea.id)}
                                className="p-2 text-slate-500 hover:text-red-400 hover:bg-white/5 rounded-lg transition-colors"
                                title="Delete"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                ))
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto custom-scrollbar pb-10 space-y-6">
          {updateLog.map((log, logIdx) => (
            <div key={logIdx} className="bg-white/5 border border-white/5 rounded-3xl overflow-hidden animate-in fade-in slide-in-from-right-4 duration-300">
               <div className="bg-indigo-500/10 p-4 border-b border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400">
                      <Sparkles size={16} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white leading-tight">{log.version}</h3>
                      <p className="text-[10px] text-indigo-300 uppercase tracking-widest font-bold">{log.date}</p>
                    </div>
                  </div>
               </div>
               <div className="p-5">
                  <ul className="space-y-3">
                    {log.changes.map((change, idx) => (
                      <li key={idx} className="flex gap-3 text-sm text-slate-300 leading-relaxed group">
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 shrink-0 group-hover:scale-125 transition-transform"></div>
                        <span>{change}</span>
                      </li>
                    ))}
                  </ul>
               </div>
            </div>
          ))}
          
          <div className="p-8 text-center border-2 border-dashed border-white/5 rounded-3xl opacity-30">
             <List size={32} className="mx-auto mb-2" />
             <p className="text-xs font-bold uppercase tracking-widest">End of History</p>
          </div>
        </div>
      )}
    </div>
  );
};